import React from 'react';
import { 
    TextField, 
    Button, 
    Accordion, 
    AccordionSummary, 
    AccordionDetails, 
    FormControl, 
    Grid, 
    Box, 
    Dialog, 
    Typography,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PaletteIcon from '@mui/icons-material/Palette';
import ImageIcon from '@mui/icons-material/Image';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import CodeIcon from '@mui/icons-material/Code';
import ThemeGallery from '../../../../components/Settings/ThemeGallery';
import ImageGallery from '../../../../components/Settings/ImageGallery';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ helpText, children }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ title, icon: Icon, children }) => {
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

const KioskStyleTab = ({ 
    settings, 
    handleInputChange,
    kioskThemes,
    foundTheme,
    globalTenantId,
    isGalleryOpen,
    handleOpenGallery,
    handleCloseGallery,
    handleImageSelect,
    handleLandscapeImageSelect,
    isAccordionExpanded,
    handleAccordionToggle,
    setPreviousCustomKioskThemeID,
    setShowThemeDialog,
    resetCustomTheme,
    setIsModified
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const isCustomTheme = settings && settings.KioskThemeID && kioskThemes && foundTheme?.shortName === 'custom';

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Theme Selection Section */}
            <SettingsSection title="Theme Selection" icon={PaletteIcon}>
                <SettingItem helpText="Choose a preset theme or select 'Custom' to create your own with custom colours and backgrounds.">
                    <FormControl variant="filled" fullWidth>
                        <ThemeGallery
                            themes={kioskThemes}
                            selectedTheme={settings.KioskThemeID}
                            onThemeSelect={(themeId) => handleInputChange({ target: { name: "KioskThemeID", value: themeId } })}
                            onCustomThemeSelect={() => {
                                if (!settings.SettingCustomKioskTheme) {
                                    setPreviousCustomKioskThemeID(settings.KioskThemeID);
                                    setShowThemeDialog(true);
                                }
                            }}
                        />
                    </FormControl>
                </SettingItem>
            </SettingsSection>

            {/* Custom Theme - Background Images */}
            {isCustomTheme && (
                <SettingsSection title="Background Images" icon={ImageIcon}>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {/* Portrait Background */}
                        <Grid item xs={12}>
                            <SettingItem helpText="Specify or choose a background image URL. Open the gallery to upload a file or use an existing external URL.">
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={8} md={9}>
                                        <TextField 
                                            fullWidth 
                                            label="Background image (Portrait)" 
                                            name="SettingCustomKioskThemeBackgroundImageURL" 
                                            value={settings.SettingCustomKioskThemeBackgroundImageURL || ''} 
                                            onChange={handleInputChange} 
                                            variant="outlined"
                                            size="medium"
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontSize: { xs: '0.9rem', sm: '0.95rem' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={3} sm={1}>
                                        {settings.SettingCustomKioskThemeBackgroundImageURL && (
                                            <Box
                                                component="img"
                                                src={settings.SettingCustomKioskThemeBackgroundImageURL} 
                                                alt="Preview" 
                                                sx={{ 
                                                    width: { xs: 40, sm: 50 }, 
                                                    height: { xs: 40, sm: 50 }, 
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                }} 
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={9} sm={3} md={2}>
                                        <Button 
                                            onClick={handleOpenGallery} 
                                            variant='outlined'
                                            fullWidth
                                            sx={{ 
                                                py: { xs: 1.25, sm: 1 },
                                                fontSize: { xs: '0.85rem', sm: '0.8rem' },
                                                textTransform: 'none'
                                            }}
                                        >
                                            Open Gallery
                                        </Button>
                                    </Grid>
                                </Grid>
                            </SettingItem>
                        </Grid>

                        {/* Landscape Background */}
                        <Grid item xs={12}>
                            <SettingItem helpText="Specify or choose a background image URL for landscape view.">
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={8} md={9}>
                                        <TextField 
                                            fullWidth 
                                            label="Background image (Landscape)" 
                                            name="SettingCustomKioskThemeLandscapeBackgroundImageURL" 
                                            value={settings.SettingCustomKioskThemeLandscapeBackgroundImageURL || ''} 
                                            onChange={handleInputChange} 
                                            variant="outlined"
                                            size="medium"
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontSize: { xs: '0.9rem', sm: '0.95rem' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={3} sm={1}>
                                        {settings.SettingCustomKioskThemeLandscapeBackgroundImageURL && (
                                            <Box
                                                component="img"
                                                src={settings.SettingCustomKioskThemeLandscapeBackgroundImageURL} 
                                                alt="Preview" 
                                                sx={{ 
                                                    width: { xs: 40, sm: 50 }, 
                                                    height: { xs: 40, sm: 50 }, 
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                }} 
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={9} sm={3} md={2}>
                                        <Button 
                                            onClick={handleOpenGallery} 
                                            variant='outlined'
                                            fullWidth
                                            sx={{ 
                                                py: { xs: 1.25, sm: 1 },
                                                fontSize: { xs: '0.85rem', sm: '0.8rem' },
                                                textTransform: 'none'
                                            }}
                                        >
                                            Open Gallery
                                        </Button>
                                    </Grid>
                                </Grid>
                            </SettingItem>
                        </Grid>
                    </Grid>

                    <Dialog
                        open={isGalleryOpen}
                        onClose={handleCloseGallery}
                        fullWidth
                        maxWidth="lg"
                    >
                        <ImageGallery
                            onImageSelect={handleImageSelect}
                            tenantId={globalTenantId}
                            pickerMode={true}
                        />
                    </Dialog>
                </SettingsSection>
            )}

            {/* Custom Theme - Colours */}
            {isCustomTheme && (
                <SettingsSection title="Theme Colours" icon={ColorLensIcon}>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {[1, 2, 3, 4, 5, 6].map((num) => (
                            <Grid item xs={12} sm={6} key={`colour-${num}`}>
                                <SettingItem helpText={`Choose theme colour ${num} using the colour picker or enter a hex value.`}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={8} sm={9}>
                                            <TextField 
                                                fullWidth 
                                                label={`Theme colour ${num}`}
                                                name={`SettingCustomKioskThemeColour${num}`}
                                                value={settings[`SettingCustomKioskThemeColour${num}`] || ''} 
                                                onChange={handleInputChange} 
                                                variant="outlined"
                                                size="medium"
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        fontSize: { xs: '0.9rem', sm: '0.95rem' }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4} sm={3}>
                                            <TextField
                                                type="color"
                                                value={settings[`SettingCustomKioskThemeColour${num}`] || '#ffffff'}
                                                onChange={handleInputChange}
                                                name={`SettingCustomKioskThemeColour${num}`}
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        p: { xs: 0.5, sm: 1 },
                                                        height: { xs: 40, sm: 40 },
                                                        cursor: 'pointer'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SettingItem>
                            </Grid>
                        ))}
                    </Grid>
                </SettingsSection>
            )}

            {/* Custom Theme - Advanced CSS */}
            {isCustomTheme && (
                <SettingsSection title="Advanced CSS" icon={CodeIcon}>
                    <Accordion 
                        expanded={isAccordionExpanded} 
                        onChange={handleAccordionToggle}
                        elevation={0}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            '&:before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel-css-content"
                            id="panel-css-header"
                        >
                            <Typography sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem' } }}>
                                {isAccordionExpanded ? 'Hide CSS Editor' : 'Show CSS Editor'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontSize: { xs: '0.8rem', sm: '0.85rem' }, 
                                    mb: 2,
                                    color: 'text.secondary'
                                }}
                            >
                                Edit the entire layout and style of the kiosk with CSS. Use #colour1#, #colour2# etc. to reference theme colours that can be changed with the selectors above.
                            </Typography>
                            <TextField
                                fullWidth
                                label="Custom Kiosk Theme CSS"
                                name="SettingCustomKioskTheme"
                                value={settings.SettingCustomKioskTheme || ''}
                                onChange={handleInputChange}
                                variant="outlined"
                                multiline 
                                minRows={6}
                                maxRows={20}
                                onKeyDown={(e) => {
                                    if (e.key === 'Tab') {
                                        e.preventDefault();
                                        const textarea = e.target;
                                        const start = textarea.selectionStart;
                                        const end = textarea.selectionEnd;
                                        textarea.value = textarea.value.substring(0, start) + '\t' + textarea.value.substring(end);
                                        textarea.selectionStart = textarea.selectionEnd = start + 1;
                                    }
                                }}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'monospace',
                                        fontSize: { xs: '0.8rem', sm: '0.85rem' }
                                    }
                                }}
                            />
                        </AccordionDetails>
                    </Accordion>
                    
                    <Box sx={{ mt: 2 }}>
                        <Button 
                            onClick={() => resetCustomTheme()} 
                            variant='outlined'
                            color="warning"
                            fullWidth={isMobile}
                            sx={{ 
                                py: { xs: 1.25, sm: 1 },
                                fontSize: { xs: '0.9rem', sm: '0.875rem' },
                                textTransform: 'none'
                            }}
                        >
                            Reset Custom Theme
                        </Button>
                    </Box>
                </SettingsSection>
            )}
        </Box>
    );
};

export default KioskStyleTab;
